import {
  DEFAULT_SPEC_ANALYTICS,
  SpaceAnalyticsLocation,
  SpecAnalytics,
} from '@fullstory/analytics';
import { ActionMenu, OptionType } from '@fullstory/components';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { ObjectType, Space } from '@fullstory/proto/fs/services/dx-objects';
import { useFlags } from '@fullstory/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  UNTITLED_SECTION_ID,
  useSpaceOptions,
  useSpaceSectionOptions,
  useSpacesEnabled,
} from '../../hooks/spaces';
import { usePermissions } from '../../hooks/usePermissions';
import { SpaceActionType } from '../../types';
import { getAccessDeniedMessageString, typeSupportsFeatureCard } from '../../utils';
import { useSaveSpaceObjectModal } from './useSaveSpaceObjectModal';

export const ADD_TO_SPACE_ID = 'add-to-space';
export const TRANSFER_TO_SPACE_ID = 'transfer-to-space';
export const FEATURE_ON_SPACE_ID = 'feature-on-space';
export const SAVE_TO_SPACE_ID = 'save-to-space';

export type SpaceObjectModalProps = {
  objectId: string;
  currentSpaceId: string;
  objectType: ObjectType;
  canTransfer: boolean;
  spaceAnalyticsLocation: SpaceAnalyticsLocation;
  specAnalytics?: SpecAnalytics;
  disableOwningSpaceOption?: boolean;
  visibility?: Visibility | undefined;
  refetchOnSave?: () => void;
};

export const useSpaceObjectModal = (spaceObjectModalProps: SpaceObjectModalProps) => {
  const {
    objectId,
    currentSpaceId,
    objectType,
    canTransfer = false,
    spaceAnalyticsLocation,
    specAnalytics = DEFAULT_SPEC_ANALYTICS,
    disableOwningSpaceOption = true,
    visibility = Visibility.UNSET,
    refetchOnSave,
  } = spaceObjectModalProps;
  const flags = useFlags();
  const intl = useIntl();
  const isPrivateObject = visibility === Visibility.PRIVATE;

  const { hasSpacesFeaturePack } = useSpacesEnabled();
  const [open, setOpen] = useState(false);
  const [keepReference, setKeepReference] = useState(false);
  const [mode, setMode] = useState<SpaceActionType>('transfer');
  const [space, setSpace] = useState<Space | undefined>();

  const { canModifySpacesObjects } = usePermissions();

  const featuringEnabled = typeSupportsFeatureCard(objectType, flags);
  const {
    spaces,
    options: filteredSpaceOptions,
    selected: selectedSpace,
    setSelected: setSelectedSpace,
  } = useSpaceOptions(currentSpaceId, mode, true, featuringEnabled, disableOwningSpaceOption);

  const fetchSectionsEnabled = (!!selectedSpace || mode === 'feature') && featuringEnabled;

  const {
    options: sectionOptions,
    selected: selectedSection,
    setSelected: setSelectedSection,
  } = useSpaceSectionOptions(
    mode === 'feature' ? currentSpaceId : selectedSpace?.value,
    fetchSectionsEnabled,
  );

  const saveSpaceObjectModal = useSaveSpaceObjectModal(spaceAnalyticsLocation);

  const sectionRequired =
    (mode === 'add' && currentSpaceId === selectedSpace?.value) || mode === 'feature';

  useEffect(() => {
    setSpace(spaces.find(s => s.id === selectedSpace?.value));
  }, [selectedSpace, spaces]);

  const menuItems: React.ComponentPropsWithoutRef<typeof ActionMenu>['actions'] | undefined =
    useMemo(() => {
      const addOrFeature = hasSpacesFeaturePack
        ? {
            iconName: 'plus-circle',
            id: ADD_TO_SPACE_ID,
            disabled: isPrivateObject || !canModifySpacesObjects,
            label: intl.formatMessage({
              defaultMessage: 'Add to a space...',
              id: '14OaEI',
            }),
            tooltip: !canModifySpacesObjects ? getAccessDeniedMessageString('none') : undefined,
          }
        : {
            iconName: 'grid',
            id: FEATURE_ON_SPACE_ID,
            disabled: isPrivateObject,
            label: intl.formatMessage({
              defaultMessage: 'Add to Featured...',
              id: '3pF6lb',
            }),
          };
      const transfer = {
        iconName: 'arrow-curve',
        id: TRANSFER_TO_SPACE_ID,
        label: intl.formatMessage({ defaultMessage: 'Transfer...', id: '/bcxmw' }),
        disabled: !canTransfer || isPrivateObject,
        tooltip: !canTransfer ? getAccessDeniedMessageString('none') : undefined,
      };

      if (hasSpacesFeaturePack) {
        return [addOrFeature, transfer] as React.ComponentPropsWithoutRef<
          typeof ActionMenu
        >['actions'];
      } else {
        return [addOrFeature] as React.ComponentPropsWithoutRef<typeof ActionMenu>['actions'];
      }
    }, [intl, canTransfer, hasSpacesFeaturePack, isPrivateObject, canModifySpacesObjects]);

  const onMenuItemSelected: React.ComponentPropsWithoutRef<typeof ActionMenu>['onSelect'] =
    useCallback((id: string) => {
      switch (id) {
        case ADD_TO_SPACE_ID:
          setMode('add');
          break;
        case FEATURE_ON_SPACE_ID:
          setMode('feature');
          break;
        case TRANSFER_TO_SPACE_ID:
          setMode('transfer');
          break;
        case SAVE_TO_SPACE_ID:
          setMode('save');
          break;
        default:
          console.error('Unknown space action', id);
          return;
      }

      setOpen(true);
    }, []);

  const onSave = useCallback(async () => {
    if (!space) {
      return;
    }

    await saveSpaceObjectModal({
      mode,
      objectId,
      objectType,
      space,
      keepReference,
      section:
        selectedSection?.value === UNTITLED_SECTION_ID
          ? { name: selectedSection.label, index: 0 }
          : selectedSection?.value,
      specAnalytics,
      sourceSpaceId: currentSpaceId,
    });
    setOpen(false);
    setSelectedSpace(null);
    setSelectedSection(null);
    if (refetchOnSave) {
      refetchOnSave();
    }
  }, [
    currentSpaceId,
    keepReference,
    mode,
    objectId,
    objectType,
    refetchOnSave,
    saveSpaceObjectModal,
    selectedSection?.label,
    selectedSection?.value,
    setSelectedSection,
    setSelectedSpace,
    space,
    specAnalytics,
  ]);

  const onCloseSignal = useCallback(() => {
    setOpen(false);
    setSelectedSpace(null);
    setSelectedSection(null);
    setKeepReference(false);
  }, [setSelectedSection, setSelectedSpace]);

  const onSpaceSelect = useCallback(
    (opt: OptionType<string> | null) => {
      setSelectedSpace(opt);
      setSelectedSection(null);
    },
    [setSelectedSection, setSelectedSpace],
  );

  return {
    menuItems,
    onMenuItemSelected,
    open,
    onCloseSignal,

    space,
    spaces,
    selectedSpace,
    onSpaceSelect,
    selectedSection,
    onSectionSelect: setSelectedSection,
    sectionRequired,
    keepReference,
    onKeepReferenceChange: setKeepReference,
    onSave,

    mode,
    spaceOptions: filteredSpaceOptions,
    sectionOptions,
  };
};
